import React from 'react';
import { Autocomplete, TextField, Box, Chip, Skeleton } from '@mui/material';
import SensorsIcon from '@mui/icons-material/Sensors';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useGetUnitsByAccountQuery } from '@/api/recalc/endpoints/unitsApiSlice';

/**
 * Autocomplete for selecting units, with custom rendering for tags and sensors.
 * @description
 * Autocomplete for selecting units, with custom rendering for tags and sensors.
 * This component reads and writes the selected units to the URL search
 * parameter `units` as a comma-separated list of UnitId values.
 */
const DashboardUnitSelector = () => {
  const { t } = useTranslation('view-data');
  const { accountId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlUnits = searchParams.get('units');
  const { data: units, isFetching: isFetching } = useGetUnitsByAccountQuery(
    accountId,
    {
      skip: !accountId,
    },
  );

  // selected units as objects (mapped from units data using url param)
  const [selected, setSelected] = React.useState([]);

  // sync selected from URL + loaded units
  React.useEffect(() => {
    if (!units) return;
    if (!urlUnits) {
      setSelected([]);
      return;
    }
    const ids = urlUnits
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const next = units.filter((u) => ids.includes(String(u.UnitId)));
    setSelected(next);
  }, [units, urlUnits]);

  const applyToUrl = (newValue) => {
    const next = new URLSearchParams(searchParams);
    if (newValue && newValue.length) {
      const v = newValue.map((u) => String(u.UnitId)).join(',');
      next.set('units', v);
    } else {
      next.delete('units');
    }
    setSearchParams(next);
  };
  if (isFetching) {
    return (
      <Skeleton>
        <TextField fullWidth />
      </Skeleton>
    );
  }
  return (
    <Autocomplete
      id="unit-autocomplete"
      multiple
      fullWidth
      clearOnBlur
      options={units || []}
      getOptionLabel={(unit) => `${unit.Name} (${unit.UnitId})` || ''}
      filterOptions={(options, { inputValue }) => {
        const search = inputValue.trim().toLowerCase();
        return options.filter((unit) => {
          const nameMatch = unit.Name?.toLowerCase().includes(search);
          const tagMatch = unit.Tags?.some((tag) =>
            tag.Text?.toLowerCase().includes(search),
          );
          const sensorMatch = unit.UnitSensors?.some((s) =>
            String(s.Sensor?.SensorId).includes(search),
          );
          const unitIdMatch = String(unit.UnitId).includes(search);
          return nameMatch || tagMatch || sensorMatch || unitIdMatch;
        });
      }}
      value={selected}
      onChange={(event, newValue) => {
        setSelected(newValue || []);
        applyToUrl(newValue || []);
      }}
      renderOption={(props, unit) => {
        const tagTexts = unit.Tags
          ? Array.from(new Set(unit.Tags.map((t) => t.Text)))
          : [];
        const sensorIds = unit.UnitSensors
          ? Array.from(
              new Set(
                unit.UnitSensors.map((s) => s.Sensor?.SensorId).filter(Boolean),
              ),
            )
          : [];
        return (
          <li {...props} key={props.key}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip label={`${unit.Name} (${unit.UnitId})`} />
              {tagTexts.map((tag) => (
                <Chip key={tag} label={tag} color="info" size="small" />
              ))}
              {sensorIds.map((sid) => (
                <Chip
                  key={sid}
                  icon={<SensorsIcon fontSize="small" />}
                  label={sid}
                  color="success"
                  size="small"
                />
              ))}
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={`${t('units')}`}
          placeholder={t('search')}
        />
      )}
    />
  );
};

export default DashboardUnitSelector;
